import { DeviceHandle, ScaleFrameFormat } from '../types'

interface SerialPortLike {
  getInfo: () => { usbVendorId?: number; usbProductId?: number }
  open: (o: { baudRate: number; dataBits?: number; stopBits?: number; parity?: string }) => Promise<void>
  close: () => Promise<void>
  readable: ReadableStream<Uint8Array> | null
  writable: WritableStream<Uint8Array> | null
}
interface Serial {
  requestPort: (o?: { filters?: { usbVendorId: number }[] }) => Promise<SerialPortLike>
  getPorts: () => Promise<SerialPortLike[]>
}

export const webSerial = (): Serial | undefined =>
  typeof navigator === 'undefined' ? undefined : (navigator as unknown as { serial?: Serial }).serial

export type { SerialPortLike }

export const serialHandle = (port: SerialPortLike): NonNullable<DeviceHandle['serial']> => {
  const info = port.getInfo()
  return { usb_vendor_id: info.usbVendorId, usb_product_id: info.usbProductId }
}

export const pickSerialPort = async (vendorIds: number[] = []) => {
  const serial = webSerial()
  if (!serial) throw new Error('This browser cannot reach serial devices; use Chrome or Edge')
  return serial.requestPort(vendorIds.length ? { filters: vendorIds.map((usbVendorId) => ({ usbVendorId })) } : undefined)
}

export const findSerialPort = async (h: NonNullable<DeviceHandle['serial']>) => {
  const ports = (await webSerial()?.getPorts()) ?? []
  const port = ports.find((p) => {
    const i = p.getInfo()
    return (!h.usb_vendor_id || i.usbVendorId === h.usb_vendor_id) && (!h.usb_product_id || i.usbProductId === h.usb_product_id)
  })
  if (!port) throw new Error('The serial device is not connected (or this browser has not been allowed to use it)')
  return port
}

/** Writes bytes (serial receipt printers), opening the port at the model's baud rate. */
export const sendSerial = async (h: NonNullable<DeviceHandle['serial']>, data: Uint8Array, baudRate = 9600) => {
  const port = await findSerialPort(h)
  await port.open({ baudRate })
  try {
    const writer = port.writable!.getWriter()
    await writer.write(data)
    writer.releaseLock()
  } finally {
    await port.close()
  }
}

/**
 * Streams a scale's text output to `onText` until `stop()` is called; sends the model's `request` on an interval for
 * scales that answer only when asked.
 */
export const readSerialScale = async (h: NonNullable<DeviceHandle['serial']>, frame: ScaleFrameFormat, onText: (text: string) => void) => {
  const port = await findSerialPort(h)
  await port.open({ baudRate: frame.baud_rate, dataBits: frame.data_bits, stopBits: frame.stop_bits, parity: frame.parity })
  const reader = port.readable!.getReader()
  const decoder = new TextDecoder()
  let stopped = false
  let poll: ReturnType<typeof setInterval> | undefined
  if (frame.request) {
    const writer = port.writable!.getWriter()
    const ask = new TextEncoder().encode(frame.request)
    poll = setInterval(() => writer.write(ask).catch(() => undefined), 300)
  }
  ;(async () => {
    let buffer = ''
    while (!stopped) {
      const { value, done } = await reader.read()
      if (done) break
      buffer = (buffer + decoder.decode(value)).slice(-512)
      onText(buffer)
    }
  })().catch(() => undefined)
  return async () => {
    stopped = true
    if (poll) clearInterval(poll)
    await reader.cancel().catch(() => undefined)
    reader.releaseLock()
    await port.close().catch(() => undefined)
  }
}
